import axios from 'axios';
import { featureFlags, setTenantFeatures } from '../utils/featureFlags';
import { getAppMarket, getJurisdictionsForMarket } from './MarketService';
import { classifyHttpError } from './httpError';

const GDPR_BASE = process.env.REACT_APP_GDPR_PRO_URL || 'http://localhost:3001';
const LANDLORD_BASE = process.env.REACT_APP_LANDLORD_PRO_URL || 'http://localhost:3002';
const FOOD_BASE = process.env.REACT_APP_FOOD_SAFETY_PRO_URL || 'http://localhost:3003';
const ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || LANDLORD_BASE;

console.log('[API] Base URLs:', {
  LANDLORD_BASE,
  ADMIN_API_URL,
  ENV: process.env.REACT_APP_ENV
});

// Create dedicated API clients
const ensureTrailingSlash = (url: string) => url.endsWith('/') ? url : `${url}/`;

export const gdprApi: any = axios.create({ baseURL: ensureTrailingSlash(GDPR_BASE) });
export const landlordApi: any = axios.create({ baseURL: ensureTrailingSlash(LANDLORD_BASE) });
export const foodSafetyApi: any = axios.create({ baseURL: ensureTrailingSlash(FOOD_BASE) });
export const adminApi: any = axios.create({ baseURL: ensureTrailingSlash(ADMIN_API_URL) });

// Partner Delete API helper (some routes might be missing or in partners.js)
export const superAdminService = {
  getUsers: () => adminApi.get('api/super-admin/users'),
  getUserDetails: (email: string) => adminApi.get(`api/super-admin/users/${email}`),
  impersonate: (email: string) => adminApi.post('api/super-admin/impersonate', { email }),
  getTenantFeatures: (tenantId: string) => adminApi.get(`api/super-admin/tenants/${tenantId}/features`),
  updateTenantFeatures: (tenantId: string, features: any) => adminApi.put(`api/super-admin/tenants/${tenantId}/features`, features),
  getAllProperties: () => adminApi.get('api/super-admin/properties'),
  getPartners: () => adminApi.get('api/super-admin/partners'),
  verifyPartner: (id: string, status: string, notes?: string) => adminApi.post(`api/super-admin/partners/${id}/verify`, { status, notes }),
  getRegulations: () => adminApi.get('api/super-admin/regulations'),
  createRegulation: (data: any) => adminApi.post('api/super-admin/regulations', data),
  updateRegulation: (id: string, data: any) => adminApi.put(`api/super-admin/regulations/${id}`, data),
  deleteRegulation: (id: string) => adminApi.delete(`api/super-admin/regulations/${id}`),
  getAuditLogs: () => adminApi.get('api/super-admin/audit'),
  downloadAuditLogs: () => adminApi.get('api/super-admin/audit/export', { responseType: 'blob' }),
  invitePartner: (data: { email: string; name: string; tradeType: string; message?: string }) => adminApi.post('api/super-admin/partners/invite', data),
  getAnnouncements: () => adminApi.get('api/super-admin/announcements'),
  createAnnouncement: (data: { title: string; message: string; type?: string; emailAll?: boolean }) => adminApi.post('api/super-admin/announcements', data),
  toggleAnnouncement: (id: string, active: boolean) => adminApi.patch(`api/super-admin/announcements/${id}/toggle`, { active }),
  deleteAnnouncement: (id: string) => adminApi.delete(`api/super-admin/announcements/${id}`),
  getSummary: () => adminApi.get('api/super-admin/dashboard/summary'),
  getAffiliates: () => adminApi.get('api/super-admin/affiliates'),
  getRegulatorRequests: () => adminApi.get('api/super-admin/regulator-requests'),
  approveRegulatorRequest: (id: string) => adminApi.post(`api/super-admin/regulator-requests/${id}/approve`),
  deletePartner: (id: string) => adminApi.delete(`api/super-admin/partners/${id}`),
  issueStrike: (id: string, reason: string) => adminApi.post(`api/super-admin/partners/${id}/strike`, { reason }),
  issueGreenCard: (id: string, reason: string) => adminApi.post(`api/super-admin/partners/${id}/strike`, { reason, cardType: 'green' }),
  deleteUser: (username: string) => adminApi.delete(`api/super-admin/users/${username}`),
  forcePasswordReset: (username: string) =>
    adminApi.post(`api/super-admin/users/${encodeURIComponent(username)}/force-password-reset`),
  // Password renewal policy (v2.5.4) — Super Admin only
  getPasswordPolicy: () => adminApi.get(`api/super-admin/password-policy?_=${Date.now()}`),
  updatePasswordPolicy: (data: { maxAgeDays: number; warnDays: number }) =>
    adminApi.put('api/super-admin/password-policy', data),
  // Per-tenant password policy overrides (v2.5.5) — Super Admin only
  getTenantPasswordPolicy: (tenantPk: string) =>
    adminApi.get(`api/super-admin/tenants/${encodeURIComponent(tenantPk)}/password-policy?_=${Date.now()}`),
  setTenantPasswordPolicy: (tenantPk: string, data: { maxAgeDays: number; warnDays: number }) =>
    adminApi.put(`api/super-admin/tenants/${encodeURIComponent(tenantPk)}/password-policy`, data),
  clearTenantPasswordPolicy: (tenantPk: string) =>
    adminApi.delete(`api/super-admin/tenants/${encodeURIComponent(tenantPk)}/password-policy`),
  // System feature flags — global visibility toggles for premium features
  // (v2.5.6). Single record backend with per-key PUT.
  getSystemFlags: () => adminApi.get(`api/super-admin/system/flags?_=${Date.now()}`),
  setSystemFlag: (key: string, enabled: boolean) =>
    adminApi.put(`api/super-admin/system/flags/${encodeURIComponent(key)}`, { enabled }),
  extendTrial: (tenantId: string, email: string, days: number) => adminApi.post(`api/super-admin/users/${tenantId}/extend-trial`, { email, days }),
  getTenants: () => adminApi.get('api/super-admin/tenants'),
  exterminateTenant: (tenantId: string, confirmText: string) =>
    adminApi.delete(`api/super-admin/tenants/${tenantId}/exterminate`, { data: { confirmText } }),
  promoteToAdmin: (username: string) => adminApi.post(`api/super-admin/users/${username}/promote-to-admin`),
  getPlanGateSetting: () => adminApi.get('api/super-admin/system/plan-gate'),
  updatePlanGateSetting: (enabled: boolean) => adminApi.put('api/super-admin/system/plan-gate', { enabled }),
  // Archived tenants (orphan-account recovery)
  getArchivedTenants: () => adminApi.get(`api/super-admin/archived-tenants?_=${Date.now()}`),
  hardDeleteArchivedTenant: (tenantPk: string) =>
    adminApi.post(`api/super-admin/archived-tenants/${encodeURIComponent(tenantPk)}/hard-delete`, { confirmEmail: 'DELETE' }),
  restoreArchivedTenant: (tenantPk: string, email: string) =>
    adminApi.post(`api/super-admin/archived-tenants/${encodeURIComponent(tenantPk)}/restore`, { email }),
};

// Partner Portal API (uses partner JWT, not Cognito)
export const partnerService = {
  getProfile: () => {
    const token = localStorage.getItem('tradePartnerToken');
    return landlordApi.get('api/partners/profile', {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  submitOnboarding: (data: any) => {
    const token = localStorage.getItem('tradePartnerToken');
    return landlordApi.put('api/partners/profile/onboarding', data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  // Backend routes/partners.js defines GET /all but is never mounted, so
  // api/partners/all 404s; api/super-admin/partners is the mounted route with
  // the same { partners, count } shape.
  listVerified: () => adminApi.get('api/super-admin/partners'),
  assignJob: (partnerId: string, data: { propertyId: string; serviceType: string; scheduledDate: string }) =>
    landlordApi.post(`api/partners/${partnerId}/assign`, data),
};

// default export object (does not redeclare)
const apis = { landlordApi, foodSafetyApi, gdprApi, adminApi };
export default apis;

// ─────────────────────────────────────────────────────────────────────
// Auth + token-refresh interceptor
// ─────────────────────────────────────────────────────────────────────

/** Decode the exp claim from a JWT. Returns ms epoch, or 0 if unreadable. */
const tokenExpiryMs = (token: string | null): number => {
  if (!token) return 0;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (payload.exp || 0) * 1000;
  } catch {
    return 0;
  }
};

/**
 * Refresh Cognito tokens via the refresh token stored by amazon-cognito-identity-js.
 * Updates localStorage with fresh idToken / accessToken. Resolves with new idToken
 * or null if refresh failed (e.g. refresh token expired → user must log in again).
 *
 * Concurrent calls share a single in-flight refresh promise to avoid stampedes.
 */
let refreshInFlight: Promise<string | null> | null = null;

const refreshSession = (): Promise<string | null> => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = new Promise<string | null>((resolve) => {
    // Lazy import to avoid circular deps
    import('./cognito').then(({ getCurrentUser }) => {
      const user = getCurrentUser();
      if (!user) {
        console.warn('[Auth] No current user for refresh');
        return resolve(null);
      }

      // CognitoUser.getSession() auto-refreshes via the stored refresh token
      // when the access token is expired.
      (user as any).getSession((err: any, session: any) => {
        if (err || !session?.isValid?.()) {
          console.warn('[Auth] Refresh failed:', err?.message || 'invalid session');
          return resolve(null);
        }
        try {
          const newIdToken     = session.getIdToken().getJwtToken();
          const newAccessToken = session.getAccessToken().getJwtToken();
          localStorage.setItem('idToken',     newIdToken);
          localStorage.setItem('accessToken', newAccessToken);
          console.log('[Auth] Token refreshed silently.');
          resolve(newIdToken);
        } catch (e: any) {
          console.error('[Auth] Failed to extract refreshed tokens:', e.message);
          resolve(null);
        }
      });
    }).catch(() => resolve(null));
  }).finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight!;
};

/** Returns the currently usable token; proactively refreshes if expiring in <60s. */
const getAuthToken = async (): Promise<string | null> => {
  // Impersonation tokens are minted by the backend and bypass refresh
  const imp = localStorage.getItem('impersonate_token');
  if (imp) return imp;

  let token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
  if (!token) return null;

  // Proactive refresh — if expiring within 60s, refresh first
  const exp = tokenExpiryMs(token);
  if (exp && exp - Date.now() < 60_000) {
    const refreshed = await refreshSession();
    if (refreshed) {
      token = localStorage.getItem('accessToken') || refreshed;
    }
  }

  return token;
};

const addAuthInterceptor = (apiClient: any) => {
  // Request: attach a fresh token (proactively refreshes if expiring)
  apiClient.interceptors.request.use(async (config: any) => {
    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // F-266: X-Jurisdiction header - value matches the currently selected
    // jurisdiction (persisted by JurisdictionProvider). No-op when unset.
    const jurisdiction = localStorage.getItem('selectedJurisdiction');
    if (jurisdiction) {
      try {
        const parsed = JSON.parse(jurisdiction);
        config.headers['X-Jurisdiction'] = parsed.abbreviation || parsed.id;
      } catch {
        // ignore invalid jurisdiction
      }
    }
    return config;
  });

  // Response: on 401, attempt one silent refresh + retry the original request
  apiClient.interceptors.response.use(
    (res: any) => res,
    async (error: any) => {
      const original = error?.config;
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      // UX-11: tag every rejected request with a single, total classification
      // so any caller can tell a request-shape failure (422/400 → validation)
      // from a real fault — and so nothing downstream can ever mistake a
      // transport error for a compliance violation. Additive and non-fatal:
      // a classification failure must never change the rejection path.
      try {
        const classified = classifyHttpError(error);
        Object.defineProperty(error, '__httpClass', {
          value: classified,
          enumerable: false,
          configurable: true,
        });
      } catch {
        /* ignore — never block the rejection on classification */
      }

      // 403 PASSWORD_EXPIRED — backend's passwordAgeGate refusing further
      // API calls until the user resets. Route them through the change-password
      // flow without burning a refresh on this request.
      if (status === 403 && code === 'PASSWORD_EXPIRED') {
        try {
          // Avoid redirect loop if we're already on the reset page
          if (!/\/(reset-password|change-password)/.test(window.location.pathname)) {
            sessionStorage.setItem('passwordExpiredAt', new Date().toISOString());
            window.location.assign('/reset-password?reason=expired');
          }
        } catch {
          // Ignore — let the error bubble to the caller
        }
        return Promise.reject(error);
      }

      // Bail if no original, not a 401, already retried, or impersonating
      if (
        !original ||
        status !== 401 ||
        original._retried ||
        localStorage.getItem('impersonate_token')
      ) {
        return Promise.reject(error);
      }

      original._retried = true;
      const newToken = await refreshSession();
      if (!newToken) {
        // Refresh failed — clear stale tokens. App will redirect on next route check.
        localStorage.removeItem('idToken');
        localStorage.removeItem('accessToken');
        return Promise.reject(error);
      }

      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newToken}`;
      return apiClient.request(original);
    }
  );
};

// Apply to all
addAuthInterceptor(adminApi);
addAuthInterceptor(landlordApi);
addAuthInterceptor(foodSafetyApi);
addAuthInterceptor(gdprApi);

// API response types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

export interface ComplianceStatus {
  overall: string;
  score: number;
  lastUpdated: string;
}

export interface DSARResponse {
  id: string;
  type: string;
  userEmail: string;
  description: string;
  status: string;
  submittedAt: string;
  estimatedCompletion: string;
  priority: string;
}

export interface CertificateResponse {
  id: string;
  name: string;
  type: string;
  propertyId: string;
  status: string;
  issued: string;
  expires: string;
  uploadedAt: string;
}

export interface HealthResponse {
  status: string;
  service: string;
  timestamp: string;
  stage?: string;
  environment?: string;
}


// SUBSCRIPTION TYPES

export interface Plan {
  price: number;
  fastspringProductPath: string;
  name: string;
  features: string[];
}

export interface Product {
  id: string;
  name: string;
  description: string;
  dashboardPath: string;
  plans: {
    basic: Plan;
    pro: Plan;
    enterprise: Plan;
  };
}

export interface Subscription {
  tenant_id: string;
  product_id: string;
  user_id: string;
  plan_tier: string;
  status: string;
  productName: string;
  planName: string;
  created_at: string;
  next_charge_date?: string;
}

export interface CheckoutResponse {
  success: boolean;
  checkoutUrl: string;
  popupData: {
    products: Array<{ path: string; quantity: number }>;
    tags: Record<string, string>;
    contact: { email: string };
  };
  storeId: string;
  productPath: string;
  successUrl: string;
  cancelUrl: string;
}


export const featureService = {
  getFeatures: () => landlordApi.get('api/settings/features'),
};

export interface BrandingProfile {
  orgName: string;
  fromName: string;
  replyToEmail?: string | null;
  supportEmail?: string | null;
  supportPhone?: string | null;
  logoUrl?: string | null;     // populated from logoS3Key on read; manual paste still works
  logoS3Key?: string | null;   // when set, backend regenerates fresh presigned logoUrl per call
  brandColor: string;
  footerText: string;
  websiteUrl?: string | null;
  // The number this org tells tenants to message on WhatsApp for hazard
  // reports. Contact-details only — inbound WhatsApp still all arrives on
  // one shared platform number and is routed to a landlord by matching the
  // tenant's own phone number, not by which number they messaged.
  whatsappNumber?: string | null;
  whatsappConfirmed?: boolean;
  whatsappConfirmedAt?: string | null;
}

export const brandingService = {
  get:    ()                          => landlordApi.get('api/settings/branding'),
  update: (data: Partial<BrandingProfile>) =>
    landlordApi.put('api/settings/branding', data),
  // Returns { uploadUrl (PUT here), s3Key (save to BrandingProfile.logoS3Key) }
  getLogoPresignedUrl: (data: { fileName: string; fileType: string }) =>
    landlordApi.post('api/settings/branding/logo/presigned-url', data),
  confirmWhatsappNumber: () =>
    landlordApi.post('api/settings/branding/whatsapp/confirm', {}),
};

// PRS DATABASE SERVICE (E-015 / F-039)
export interface PrsReadinessItem {
  propertyId: string;
  addressLine1: string;
  status: 'GREEN' | 'AMBER' | 'RED';
  score: number;
  missingRequired: { key: string; label: string }[];
  missingOptional: { key: string; label: string }[];
}
export interface PrsReadinessReport {
  items: PrsReadinessItem[];
  summary: {
    total: number;
    green: number;
    amber: number;
    red: number;
    averageScore: number;
    readinessPercent: number;
  };
}
export interface PrsImportResult {
  total: number;
  updated: number;
  skipped: number;
  notMatched: number;
  /** Total number of read-only CSV cells the user changed but the server
   *  ignored (e.g. cert expiry dates edited in the spreadsheet). Helps the
   *  popup tell the user where the edit should be made instead. */
  ignoredEdits: number;
  /** Top-3 read-only columns the user tried to edit, each tagged with the
   *  UI surface that actually owns that field. */
  ignoredFieldHints: { key: string; count: number; ownerUi: string }[];
  details: {
    propertyId: string | null;
    address: string | null;
    status: string;
    fields?: string[];
    ignoredFields?: string[];
    error?: string;
  }[];
}
export interface PrsImportJobStatus {
  jobId: string;
  landlordId: string;
  status: string;
  result?: PrsImportResult;
  error?: string;
  createdAt: string;
  updatedAt: string;
}
export const prsService = {
  getReadiness: () =>
    landlordApi.get('api/prs/readiness'),
  // Browser-side download — returns a Blob so the caller can trigger the
  // save-as dialog without round-tripping through JSON. Pass propertyId to
  // export a single property; omit for the whole portfolio.
  exportCsv: (propertyId?: string) => {
    const url = propertyId ? `api/prs/export/${propertyId}` : 'api/prs/export';
    return landlordApi.get(url, { responseType: 'blob' });
  },
  // Round-trip: send the edited CSV back as raw text. Returns a jobId
  // immediately; the backend processes asynchronously.
  importCsv: (csvText: string) =>
    landlordApi.post(
      'api/prs/import',
      csvText,
      { headers: { 'Content-Type': 'text/csv' } },
    ),
  getImportStatus: (jobId: string) =>
    landlordApi.get(
      `api/prs/import/status/${jobId}`,
    ),
};

// ACCOUNT & AUDIT SERVICE
export const accountService = {
  getProfile: () => adminApi.get('api/account'),
  updateProfile: (data: any) => adminApi.put('api/account', data),
  getAuditLogs: (params?: any) => adminApi.get('api/audit', { params }),
  logAuditEvent: (data: { action: string; severity?: string; details?: any }) =>
    landlordApi.post('api/audit', data),
  // Password renewal policy (v2.5.4)
  markPasswordChanged: () => adminApi.post('api/account/me/password-changed'),
  getPasswordStatus: () => adminApi.get('api/account/me/password-status'),
};

// AML SERVICE (F-005)
export const amlService = {
  getChecks: () => landlordApi.get('api/aml/checks'),
  getCheck: (id: string) => landlordApi.get(`api/aml/checks/${id}`),
  recordCheck: (data: any) => landlordApi.post('api/aml/checks', data),
  bulkImportChecks: (checks: any[]) => landlordApi.post('api/aml-bulk/checks', { checks }),
  updateCheck: (id: string, data: any) => landlordApi.put(`api/aml/checks/${id}`, data),
  deleteCheck: (id: string) => landlordApi.delete(`api/aml/checks/${id}`),
  verifyCheck: (id: string) => landlordApi.get(`api/aml/checks/${id}/verify`),
  exportTrail: () => landlordApi.get('api/aml/export'),
  getMethods: () => landlordApi.get('api/aml/methods'),
  listByProperty: (propertyId: string) => landlordApi.get(`api/aml/checks/property/${propertyId}`),
  recordVerification: (data: any) => landlordApi.post('api/aml/verifications', data),
  verifyIntegrity: (propertyId: string, recordId: string) => landlordApi.get(`api/aml/checks/${recordId}/verify?propertyId=${propertyId}`),
};

// SUBSCRIPTION SERVICE
export const subscriptionService = {
  // Backend mounts the router at /api/subscription (singular). The
  // previous paths (subscriptions / subscriptions/plans) were 404ing
  // in prod — surfaced on the AddProperty page where the prerequisite
  // check calls getMySubscriptions() at mount.

  // Get current tier + config for the authed user (returns the same
  // payload the UI uses for both "my subs" and "available plans" — the
  // backend doesn't expose a separate /plans endpoint).
  getPlans: () => adminApi.get('api/subscription'),
  getMySubscriptions: () => adminApi.get('api/subscription'),

  // Create FastSpring checkout link for a target tier.
  // Backend expects { targetTier } at POST /api/subscription/checkout.
  createCheckout: (productId: string, planTier: string) =>
    adminApi.post('api/subscription/checkout', { targetTier: planTier, productId }),
};


// SUPPORT SERVICE
export const supportService = {
  createTicket: (data: { subject: string; message: string; priority: string }) => adminApi.post('api/support/tickets', data),
  getTickets: () => adminApi.get('api/support/tickets'),
};

// TEAM SERVICE (Multi-User)
export const teamService = {
  getUsers: () => landlordApi.get('api/team'),
  inviteUser: (data: { email: string; name: string; role: string }) => landlordApi.post('api/team/invite', data),
  invitePartner: (data: { email: string; name: string; tradeType: string; message?: string }) => landlordApi.post('api/partners/invite', data),
  removeUser: (userId: string) => landlordApi.delete(`api/team/${userId}`)
};

// TENANT SERVICE (Tenant Management)
export const tenantService = {
  getTenants: () => landlordApi.get('api/tenants'),
  getTenant: (id: string) => landlordApi.get(`api/tenants/${id}`),
  getTenantsByProperty: (propertyId: string) => landlordApi.get(`api/tenants/property/${propertyId}`),
  addTenant: (data: {
    name: string;
    email: string;
    phone?: string;
    propertyId: string;
    tenancyStartDate?: string;
    tenancyEndDate?: string;
    rentAmount?: number;
    rentFrequency?: string;
    depositAmount?: number;
    depositScheme?: string;
    depositReference?: string;
    additionalTenants?: Array<{ name: string; email: string; phone: string }>;
    isJointTenancy?: boolean;
    complianceException?: boolean;
    complianceExceptionReason?: string;
    complianceExceptionRemediationDate?: string;
    rentPaymentDay?: number;
    rentPaymentDayChangedDate?: string;
    freePeriodGranted?: boolean;
    freePeriodDays?: number;
    autoAttachRightToRent?: boolean;
    rightToRentUrl?: string;
    agreementMergeFields?: Record<string, any>;
    agreementId?: string;
  }) => landlordApi.post('api/tenants', data),
  updateTenant: (id: string, data: {
    name?: string;
    email?: string;
    phone?: string;
    propertyId?: string;
    status?: string;
    tenancyStartDate?: string;
    tenancyEndDate?: string;
    rentAmount?: number;
    rentPaymentDay?: number;
    freePeriodGranted?: boolean;
    freePeriodDays?: number;
    [key: string]: any;
  }) => landlordApi.put(`api/tenants/${id}`, data),
  deleteTenant: (id: string) => landlordApi.delete(`api/tenants/${id}`),
  checkCompliance: (propertyId: string, params: { rentAmount?: number; rentFrequency?: string; depositAmount?: number }) =>
    landlordApi.get(`api/tenancy/compliance-check/${propertyId}`, { params }),
  bulkImport: (tenants: any[]) => landlordApi.post('api/tenants/bulk', { tenants }),
};

// ARREARS SERVICE (F-019) — rent payment monitoring + RRA workflow
// Bank statement CSV import — auto-reconciles credits to LP-XXXX-NN refs
export const paymentsService = {
  importStatement: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return landlordApi.post('api/payments/import-statement', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60_000,
    });
  },
  supportedFormats: () => landlordApi.get('api/payments/supported-formats'),
};

// Payment receipts — public verify, authed download, per-tenant list
export const receiptsService = {
  // Public — used by the verification page (the QR target)
  verify:  (receiptId: string) => landlordApi.get(`api/receipts/verify/${receiptId}`),
  // Authed — list every receipt for a tenant
  list:    (tenantId: string) => landlordApi.get(`api/receipts/tenant/${tenantId}`),
  // Returns the download URL (browser opens it; auth header gets added by interceptor)
  downloadUrl: (receiptId: string) => `${LANDLORD_BASE.replace(/\/$/, '')}/api/receipts/${receiptId}`,
};

export const arrearsService = {
  getDashboard: () => landlordApi.get('api/arrears/dashboard'),
  getTenants: () => landlordApi.get('api/arrears/tenants'),
  getTenantDetail: (tenantId: string) => landlordApi.get(`api/arrears/tenant/${tenantId}`),
  recordPayment: (tenantId: string, data: {
    periodYM: string;
    paymentDate: string;
    amount: number;
    method?: string;
    reference?: string;
    note?: string;
    status?: string;
  }) => landlordApi.post(`api/arrears/tenant/${tenantId}/payment`, data),
  updatePayment: (tenantId: string, paymentId: string, data: any & { periodYM: string }) =>
    landlordApi.put(`api/arrears/tenant/${tenantId}/payment/${paymentId}`, data),
  deletePayment: (tenantId: string, paymentId: string, periodYM: string) =>
    landlordApi.delete(`api/arrears/tenant/${tenantId}/payment/${paymentId}`, { params: { periodYM } }),
  acknowledgeAlert: (tenantId: string, periodYM: string) =>
    landlordApi.post(`api/arrears/tenant/${tenantId}/acknowledge-alert`, { periodYM }),
  evaluateNotifications: () => landlordApi.post('api/arrears/evaluate-notifications', {}),
};

// RSL SAFEGUARDING (E-017 / F-047) — Pre-Action Protocol vulnerability
// assessment + MCA 2005 capacity screening. Tenant-level records.
export const rslService = {
  getSafeguardingProfile: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/safeguarding`),
  createVulnerabilityAssessment: (tenantId: string, data: any) =>
    landlordApi.post(`api/rsl/tenants/${tenantId}/vulnerability-assessment`, data),
  listVulnerabilityAssessments: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/vulnerability-assessments`),
  verifyVulnerabilityAssessment: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/vulnerability-assessments/${id}/verify`),
  createCapacityScreen: (tenantId: string, data: any) =>
    landlordApi.post(`api/rsl/tenants/${tenantId}/capacity-screen`, data),
  listCapacityScreens: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/capacity-screens`),
  verifyCapacityScreen: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/capacity-screens/${id}/verify`),
  // Equality Act 2010 reasonable adjustments (US-053 / T-047-003)
  createAdjustment: (tenantId: string, data: any) =>
    landlordApi.post(`api/rsl/tenants/${tenantId}/adjustments`, data),
  listAdjustments: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/adjustments`),
  updateAdjustment: (tenantId: string, id: string, data: any) =>
    landlordApi.put(`api/rsl/tenants/${tenantId}/adjustments/${id}`, data),
  verifyAdjustment: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/adjustments/${id}/verify`),
  // Care Act 2014 community care assessment referral (US-054 / T-047-004)
  createCareReferral: (tenantId: string, data: any) =>
    landlordApi.post(`api/rsl/tenants/${tenantId}/care-referrals`, data),
  listCareReferrals: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/care-referrals`),
  updateCareReferralOutcome: (tenantId: string, id: string, data: any) =>
    landlordApi.put(`api/rsl/tenants/${tenantId}/care-referrals/${id}/outcome`, data),
  verifyCareReferral: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/care-referrals/${id}/verify`),
  getEvidenceSafe: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/evidence-safe`),
  // US-055 / T-047-005: Protocol Part 3 proportionality schedule
  createProtocolCase: (tenantId: string, data: any) =>
    landlordApi.post(`api/rsl/tenants/${tenantId}/protocol-case`, data),
  listProtocolCases: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/protocol-cases`),
  getProtocolCase: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/protocol-cases/${id}`),
  updateProtocolCase: (tenantId: string, id: string, data: any) =>
    landlordApi.put(`api/rsl/tenants/${tenantId}/protocol-cases/${id}`, data),
  signOffProtocolCase: (tenantId: string, id: string) =>
    landlordApi.post(`api/rsl/tenants/${tenantId}/protocol-cases/${id}/sign-off`),
  verifyProtocolCase: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/protocol-cases/${id}/verify`),
  downloadSchedulePdf: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/protocol-cases/${id}/schedule.pdf`, { responseType: 'blob' }),
  // US-055 / T-047-006: Evidence bundle compiler + PDF generator
  generateEvidenceBundle: (tenantId: string, data?: any) =>
    landlordApi.post(`api/rsl/tenants/${tenantId}/evidence-bundles`, data || {}),
  listEvidenceBundles: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/evidence-bundles`),
  getEvidenceBundle: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/evidence-bundles/${id}`),
  downloadEvidenceBundlePdf: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/evidence-bundles/${id}/pdf`, { responseType: 'blob' }),
  verifyEvidenceBundle: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/evidence-bundles/${id}/verify`),
  // US-055 / T-047-007: Protocol compliance dashboard status
  getProtocolCompliance: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/protocol-compliance`),
  // US-057 / F-048: Tenancy sustainment service directory (T-048-001)
  listSupportServices: () =>
    landlordApi.get('api/rsl/services'),
  createSupportService: (data: any) =>
    landlordApi.post('api/rsl/services', data),
  updateSupportService: (id: string, data: any) =>
    landlordApi.put(`api/rsl/services/${id}`, data),
  deleteSupportService: (id: string) =>
    landlordApi.delete(`api/rsl/services/${id}`),
  // US-057 / T-048-001: Sustainment referrals with outcome tracking
  createSustainmentReferral: (tenantId: string, data: any) =>
    landlordApi.post(`api/rsl/tenants/${tenantId}/sustainment-referrals`, data),
  listSustainmentReferrals: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/sustainment-referrals`),
  updateSustainmentReferralStatus: (tenantId: string, id: string, data: any) =>
    landlordApi.put(`api/rsl/tenants/${tenantId}/sustainment-referrals/${id}/status`, data),
  recordSustainmentOutcome: (tenantId: string, id: string, data: any) =>
    landlordApi.put(`api/rsl/tenants/${tenantId}/sustainment-referrals/${id}/outcome`, data),
  verifySustainmentReferral: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/sustainment-referrals/${id}/verify`),
  // US-057 / T-048-002: Joint protocol agreements (multi-agency PDFs)
  generateJointAgreement: (tenantId: string, data: any) =>
    landlordApi.post(`api/rsl/tenants/${tenantId}/sustainment-agreements`, data),
  listJointAgreements: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/sustainment-agreements`),
  downloadJointAgreementPdf: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/sustainment-agreements/${id}/pdf`, { responseType: 'blob' }),
  verifyJointAgreement: (tenantId: string, id: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/sustainment-agreements/${id}/verify`),
  // US-057 / T-048-003: Sustainment alerts (early-warning queue)
  scanSustainmentAlerts: (tenantId: string) =>
    landlordApi.post(`api/rsl/tenants/${tenantId}/sustainment-alerts/scan`, {}),
  listSustainmentAlerts: (tenantId: string) =>
    landlordApi.get(`api/rsl/tenants/${tenantId}/sustainment-alerts`),
  updateSustainmentAlert: (tenantId: string, id: string, data: any) =>
    landlordApi.put(`api/rsl/tenants/${tenantId}/sustainment-alerts/${id}`, data),
};

// REPAIRS TRACKING (F-084 RP02) — repair job creation, closure, backlog, and dashboard.
export const repairsService = {
  bulkImport: (repairs: any[]) => landlordApi.post('api/repairs/bulk', { repairs }),
};

// COMPLAINT-REPAIRS SILO BREAKER (F-090) — auto-links repairs + complaint
// cases on SLA breach + repeated tenant contact, shared timeline, Evidence
// Safe bundle, cross-team handover gate, board silo-break metric.
export const siloBreakerService = {
  listLinks: (groupId?: string) =>
    landlordApi.get('api/silo-links', { params: groupId ? { groupId } : {} }),
  getBoardMetric: (groupId?: string) =>
    landlordApi.get('api/silo-links/board', { params: groupId ? { groupId } : {} }),
  getLink: (linkId: string) =>
    landlordApi.get(`api/silo-links/${linkId}`),
  getTimeline: (linkId: string) =>
    landlordApi.get(`api/silo-links/${linkId}/timeline`),
  getEvidenceSafe: (linkId: string) =>
    landlordApi.get(`api/silo-links/${linkId}/evidence-safe`),
  sealBundle: (linkId: string) =>
    landlordApi.post(`api/silo-links/${linkId}/seal`, {}),
  acknowledgeHandover: (linkId: string, owner: 'complaint' | 'repairs', by?: string) =>
    landlordApi.post(`api/silo-links/${linkId}/handover/acknowledge`, { owner, by }),
  getCloseGate: (linkId: string) =>
    landlordApi.get(`api/silo-links/${linkId}/close-gate`),
  closeCheck: (linkId: string) =>
    landlordApi.post(`api/silo-links/${linkId}/close-check`, {}),
};

// AWAAB'S LAW (E-018 / F-049 + F-050 + F-051 + F-054) — hazard triage,
// investigation, repair/preventative work, Reg 9 written summary, alternative
// accommodation, HHSRS classification, compliance dashboard and Evidence Safe
// bundles.
export const awaabService = {
  bulkImportCases: (cases: any[]) => landlordApi.post('api/awaab-bulk/cases', { cases }),
  getHhsrsTaxonomy: () =>
    landlordApi.get('api/awaab/hhsrs/taxonomy'),
  createCase: (tenantId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases`, data),
  listCases: (tenantId: string) =>
    landlordApi.get(`api/awaab/tenants/${tenantId}/cases`),
  getCase: (tenantId: string, caseId: string) =>
    landlordApi.get(`api/awaab/tenants/${tenantId}/cases/${caseId}`),
  verifyCase: (tenantId: string, caseId: string) =>
    landlordApi.get(`api/awaab/tenants/${tenantId}/cases/${caseId}/verify`),
  getEvidenceBundle: (tenantId: string, caseId: string) =>
    landlordApi.get(`api/awaab/tenants/${tenantId}/cases/${caseId}/evidence-bundle`),
  startInvestigation: (tenantId: string, caseId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/investigations`, data),
  completeInvestigation: (tenantId: string, caseId: string, investigationId: string, data: any) =>
    landlordApi.put(`api/awaab/tenants/${tenantId}/cases/${caseId}/investigations/${investigationId}/outcome`, data),
  // F-079: tenant right to request an in-person renewed investigation
  requestRenewedInvestigation: (tenantId: string, caseId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/renewed-investigation-request`, data),
  issueWrittenSummary: (tenantId: string, caseId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/written-summary`, data),
  createWork: (tenantId: string, caseId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/work`, data),
  setWorkStatus: (tenantId: string, caseId: string, workId: string, data: any) =>
    landlordApi.put(`api/awaab/tenants/${tenantId}/cases/${caseId}/work/${workId}/status`, data),
  offerAccommodation: (tenantId: string, caseId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/accommodation`, data),
  setAccommodationStatus: (tenantId: string, caseId: string, accommodationId: string, data: any) =>
    landlordApi.put(`api/awaab/tenants/${tenantId}/cases/${caseId}/accommodation/${accommodationId}/status`, data),
  getDashboard: () =>
    landlordApi.get('api/awaab/dashboard'),
  createHazardIntakeLink: (tenantId: string) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/intake-link`, {}),
  // F-056: Competent Person & Contractor Register
  listRegister: (type?: string) =>
    landlordApi.get('api/awaab/register', { params: type ? { type } : {} }),
  createRegisterEntry: (data: any) =>
    landlordApi.post('api/awaab/register', data),
  getRegisterEntry: (registerType: string, id: string) =>
    landlordApi.get(`api/awaab/register/${registerType}/${id}`),
  updateRegisterEntry: (registerType: string, id: string, data: any) =>
    landlordApi.put(`api/awaab/register/${registerType}/${id}`, data),
  getRegisterAssignments: (registerType: string, id: string, propertyId?: string) =>
    landlordApi.get(`api/awaab/register/${registerType}/${id}/assignments`, { params: propertyId ? { propertyId } : {} }),
  scanRegister: () =>
    landlordApi.post('api/awaab/register/scan', {}),
  // F-057: Access Refusal & Landlord Defence (Reg 7 chain)
  logAccessAttempt: (tenantId: string, caseId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/access-attempts`, data),
  getAccessAttempts: (tenantId: string, caseId: string) =>
    landlordApi.get(`api/awaab/tenants/${tenantId}/cases/${caseId}/access-attempts`),
  updateAccessAttempt: (tenantId: string, caseId: string, attemptId: string, data: any) =>
    landlordApi.put(`api/awaab/tenants/${tenantId}/cases/${caseId}/access-attempts/${attemptId}`, data),
  resumeAccess: (tenantId: string, caseId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/access-resume`, data),
  escalateAccess: (tenantId: string, caseId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/access-escalation`, data),
  generateReg7Letter: (tenantId: string, caseId: string) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/reg7-warning-letter`, {}),
  // F-082: Exclusion & Defence Wizard
  listExclusionTypes: () =>
    landlordApi.get('api/awaab/exclusion-types'),
  buildDefencePack: (tenantId: string, caseId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/defence-pack`, data),
  auditExclusion: (tenantId: string, caseId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/audit-exclusion`, data),
  reopenExclusion: (tenantId: string, caseId: string, data: any) =>
    landlordApi.post(`api/awaab/tenants/${tenantId}/cases/${caseId}/reopen-exclusion`, data),
  // F-061: Damp & Mould Register
  listDmRegister: () =>
    landlordApi.get('api/tsm/damp-mould/register'),
  createDmRegisterEntry: (data: any) =>
    landlordApi.post('api/tsm/damp-mould/register', data),
  updateDmRegisterEntry: (id: string, data: any) =>
    landlordApi.patch(`api/tsm/damp-mould/register/${id}`, data),
  exportDmRegisterCsv: () =>
    landlordApi.get('api/tsm/damp-mould/register/export.csv', { responseType: 'text' }),
  // B-012: portfolio-wide hazard-response dashboard (fixes F-051's AC).
  // Not deployed everywhere yet — callers should catch a 404 gracefully
  // and fall back to getDashboard() above until it's merged.
  getPortfolioDashboard: () =>
    landlordApi.get('api/awaab/portfolio-dashboard'),
};

// F-132: Compliance Command Centre — portfolio-wide health score (F-104)
// and the three tab data sources (Repairs & Awaab's Law, TSM Reporting,
// Complaints & Ombudsman), all reusing engines already deployed on
// staging2/prod2. No new backend routes — this just wires the frontend to
// what already exists.
export const complianceCentreService = {
  getPortfolioHealth: () =>
    landlordApi.get('api/health/portfolio'),
  getTsmDraft: (period: string, groupBasis = false) =>
    landlordApi.get('api/tsm/measures/draft', { params: { period, ...(groupBasis ? { groupBasis: 'true' } : {}) } }),
  getComplaintAnalytics: () =>
    landlordApi.get('api/chc/analytics'),
  listOmbudsmanDeterminations: () =>
    landlordApi.get('api/ombudsman-determinations'),
};

// CRM / MARKETING SERVICE (Brevo Integration)
export const crmService = {
  createContact: (data: { email: string; firstName: string; lastName: string; phone: string; units: number; marketingConsent: boolean; plan?: string }) =>
    landlordApi.post('api/marketing/contact', {
      ...data,
      listIds: [3],
      attributes: {
        PRODUCT: 'Landlord_Pro',
        // Assuming backend maps firstName/lastName/phone from root 'data' object or we duplicate them here if needed.
        // Usually Brevo expects attributes like FIRSTNAME, LASTNAME.
        FIRSTNAME: data.firstName,
        LASTNAME: data.lastName,
        SMS: data.phone,
        UNITS: data.units
      }
    }),
};

export const complianceService = {
  // GDPR Service
  getGDPRStatus: () => gdprApi.get('api/compliance/status'),  // Remove <ApiResponse<ComplianceStatus>>
  submitDSAR: (data: any) => gdprApi.post('api/compliance/dsar', data),  // Remove <ApiResponse<DSARResponse>>
  getGDPRIssues: () => gdprApi.get('api/compliance/issues'),
  getGDPRCertificates: () => gdprApi.get('api/compliance/certificates'),

  // Landlord Service
  getDashboardStats: (groupId?: string) => landlordApi.get('api/dashboard', { params: { groupId } }),

  // F-266: Jurisdictions. Backend GET /api/jurisdictions was never implemented
  // (404 on every stage) — serve the build-time market list instead of firing a
  // request that can only fail.
  getJurisdictions: () => Promise.resolve({ data: getJurisdictionsForMarket(getAppMarket()) }),

  getLandlordStatus: () => landlordApi.get('api/compliance/status'),  // Remove <ApiResponse<ComplianceStatus>>
  getExposureScore: () => landlordApi.get('api/compliance/exposure-score'),  // US-016
  getPredictiveAlerts: () => landlordApi.get('api/compliance/predictive-alerts'),  // F-012
  getLandlordIssues: () => landlordApi.get('api/compliance/issues'),
  getActiveAnnouncement: () => landlordApi.get('api/announcements/active'),
  getProperties: () => landlordApi.get('api/properties'),
  getProperty: (id: string) => landlordApi.get(`api/properties/${id}`),
  getLandlordCertificates: () => landlordApi.get('api/compliance/certificates'),
  uploadCertificate: (data: any) => landlordApi.post('api/compliance/certificates/upload', data),  // LEGACY
  // Presigned URL flow (bypasses API Gateway limits)
  getPresignedUploadUrl: (data: { fileName: string; fileType: string; propertyId: string }) =>
    landlordApi.post('api/compliance/certificates/presigned-url', data),
  confirmCertificateUpload: (data: {
    name: string;
    type: string;
    propertyId: string;
    issuedDate?: string;
    expiryDate: string;
    s3Key: string;
    fileName: string;
    fileSize?: number;
    contentHash?: string;
    uploadTimestamp?: string;
    isExpired?: boolean;
  }) =>
    landlordApi.post('api/compliance/certificates/confirm', data),
  // Attach a PDF to an EXISTING certificate (for bulk-imported metadata
  // records that have no file). Preserves cert id and audit history.
  attachFileToCertificate: (certId: string, data: {
    propertyId: string;
    s3Key: string;
    fileName: string;
    fileSize?: number;
    contentHash?: string;
  }) =>
    landlordApi.post(`api/compliance/certificates/${certId}/attach-file`, data),
  addProperty: (data: any) => landlordApi.post('api/properties', data),
  updateProperty: (id: string, data: any) => landlordApi.put(`api/properties/${id}`, data),
  deleteProperty: (id: string) => landlordApi.delete(`api/properties/${id}`),
  deletePropertiesBulk: (data: { propertyIds?: string[]; deleteAll?: boolean }) => 
    landlordApi.delete('api/properties/bulk', { data }),

  // F-310: bulk CSV import through the LP properties-bulk route (mounted at
  // ['/api/properties','/properties'], POST /bulk-upload). Sends the raw CSV
  // text as an text/csv request body; the route also accepts multipart 'file'
  // or { csv } if needed elsewhere. Auth rides the landlordApi interceptor.
  bulkUploadProperties: (csvText: string, dryRun: boolean = false) =>
    landlordApi.post(
      `api/properties/bulk-upload${dryRun ? '?dryRun=true' : ''}`,
      csvText,
      { headers: { 'Content-Type': 'text/csv' } }
    ),

  updateCertificate: (certificateId: string, propertyId: string, data: { expiryDate: string; issuedDate?: string; type?: string }) =>
    landlordApi.put(`api/compliance/certificates/${certificateId}`, { propertyId, ...data }),
  deleteCertificate: (certificateId: string, propertyId: string) =>
    landlordApi.delete(`api/compliance/certificates/${certificateId}?propertyId=${propertyId}`),
  bulkImportCertificates: (propertyId: string, certificates: any[]) => landlordApi.post(`api/compliance/${propertyId}/certificates/bulk`, { certificates }),
  bulkImportCertificatesGlobal: (certificates: any[]) => landlordApi.post('api/certificates/bulk', { certificates }),
  deleteAllProperties: () => landlordApi.delete('api/properties'),

  // F-003: Legal Updates
  getLegalUpdates: () => landlordApi.get('api/legal-updates'),

  // F-009: Acknowledgement Portal
  requestAcknowledgement: (data: { propertyId: string; tenantEmail: string; tenantName: string; documents: string[] }) =>
    landlordApi.post('api/portal/request-acknowledgement', data),
  getAcknowledgements: (propertyId: string) =>
    landlordApi.get(`api/portal/acknowledgements/${propertyId}`),
  // F-006: Generate Evidence Pack
  generateEvidencePack: (propertyId: string) => landlordApi.get(`api/evidence/${propertyId}/pack`),

  // F-015: Health Score
  getHealthScore: (propertyId: string) =>
    landlordApi.get(`api/properties/${propertyId}/health`),

  // F-016: NLP Document Analysis
  analyzeDocument: (text: string, documentType?: string) =>
    landlordApi.post('api/analysis/smart', { text, documentType: documentType || 'TENANCY_AGREEMENT' }),
  analyzeDocumentHybrid: (text: string) =>
    landlordApi.post('api/analysis/hybrid', { text }),
  analyzeFromS3: (s3Key: string, useLLM?: boolean) =>
    landlordApi.post('api/analysis/s3', { s3Key, useLLM: useLLM ?? true }),
  getAnalysisStatus: () =>
    landlordApi.get('api/analysis/status'),
  // Food Safety Service
  getFoodSafetyStatus: () => foodSafetyApi.get('api/compliance/status'),  // Remove <ApiResponse<ComplianceStatus>>
  getFoodSafetyIssues: () => foodSafetyApi.get('api/compliance/issues'),
  getHACCPChecks: () => foodSafetyApi.get('api/compliance/haccp/checks'),
  logTemperature: (data: any) => foodSafetyApi.post('api/compliance/temperature/log', data),

  // Daily Checks (Sprint 2)
  getDailyChecks: (establishmentId: string, startDate?: string, endDate?: string) =>
    foodSafetyApi.get('api/checks', { params: { establishmentId, startDate, endDate } }),
  submitDailyCheck: (data: { establishmentId: string; date: string; type: string; data: any; performedBy?: string }) =>
    foodSafetyApi.post('api/checks', data),

  // Documents (FSP)
  getPresignedDocUrl: (data: { fileName: string; fileType: string; folder?: string }) =>
    foodSafetyApi.post('api/documents/presigned-url', data),

  // Housing Pro Dashboard & Group Management
  getPropertyTree: () => landlordApi.get('api/property-groups/tree'),
  getPropertyGroups: () => landlordApi.get('api/property-groups'),
  createPropertyGroup: (data: any) => landlordApi.post('api/property-groups', data),
  updatePropertyGroup: (id: string, data: any) => landlordApi.put(`api/property-groups/${id}`, data),
  deletePropertyGroup: (id: string) => landlordApi.delete(`api/property-groups/${id}`),
  addPropertiesToGroup: (id: string, propertyIds: string[]) => landlordApi.post(`api/property-groups/${id}/properties`, { propertyIds }),
  removePropertiesFromGroup: (id: string, propertyIds: string[]) => landlordApi.delete(`api/property-groups/${id}/properties`, { data: { propertyIds } }),
  getExceptionFeed: (groupId?: string) => landlordApi.get('api/dashboard/exceptions', { params: { groupId } }),

  // Helper for direct S3 upload
  uploadToS3: (signedUrl: string, file: File) =>
    axios.put(signedUrl, file, { headers: { 'Content-Type': file.type } }),
};

// Health check functions
export const healthService = {
  checkGDPRHealth: () => gdprApi.get('health'),  // Remove <HealthResponse>
  checkLandlordHealth: () => landlordApi.get('health'),  // Remove <HealthResponse>
  checkFoodSafetyHealth: () => foodSafetyApi.get('health'),  // Remove <HealthResponse>
};

// Evidence Vault Service (F-004)
export const evidenceService = {
  // Get presigned URL for upload
  getPresignedUrl: (data: { fileName: string; fileType: string; propertyId: string }) =>
    landlordApi.post('api/evidence/presigned-url', data),

  // Confirm upload with SHA-256 hash
  confirmUpload: (data: {
    propertyId: string;
    fileName: string;
    s3Key: string;
    fileSize: number;
    fileType: string;
    contentHash: string;
    description?: string;
    type: string;
  }) => landlordApi.post('api/evidence/confirm', data),

  // List evidence for a property
  listByProperty: (propertyId: string) =>
    landlordApi.get(`api/evidence/${propertyId}`),

  // Get download URL
  getDownloadUrl: (propertyId: string, evidenceId: string) =>
    landlordApi.get(`api/evidence/${propertyId}/${evidenceId}/download`),

  // Delete evidence
  delete: (evidenceId: string, propertyId: string) =>
    landlordApi.delete(`api/evidence/${evidenceId}?propertyId=${propertyId}`),

  // Verify integrity
  verify: (propertyId: string, evidenceId: string, hash: string) =>
    landlordApi.post(`api/evidence/${propertyId}/${evidenceId}/verify`, { hash })
};

// Tenant Payment Portal Service (Sprint 7)
export const tenantPaymentService = {
    getPortal: (token: string) => landlordApi.get(`api/tenant-payments/${token}`),
    generateLink: (tenantId: string) => landlordApi.post(`api/tenant-payments/generate/${tenantId}`),
    sendLink: (tenantId: string, portalUrl?: string) =>
        landlordApi.post(`api/tenant-payments/send/${tenantId}`, { portalUrl }),
};

// Tenant Portal Service (F-009)
export const portalService = {
  // Request acknowledgement (Landlord)
  requestAcknowledgement: (data: { propertyId: string; tenantEmail: string; tenantName: string; documents: string[] }) =>
    landlordApi.post('api/portal/request-acknowledgement', data),

  // List acknowledgements (Landlord)
  listAcknowledgements: (propertyId: string) =>
    landlordApi.get(`api/portal/acknowledgements/${propertyId}`),

  // Public Tenant Endpoints
  getAcknowledgement: (token: string) => landlordApi.get(`api/portal/${token}`),
  confirmAcknowledgement: (token: string, data: { confirmedDocuments: string[]; signature: string; consent: boolean }) =>
    landlordApi.post(`api/portal/${token}/confirm`, data),
  cancelAcknowledgement: (propertyId: string, ackId: string) =>
    landlordApi.delete(`api/portal/acknowledgements/${propertyId}/${ackId}`),

  // Public Tenant Hazard Reporting (F-055 / US-070)
  getHazardIntakeConfig: (token: string) =>
    landlordApi.get(`api/portal/hazard-report/${token}`),
  presignHazardPhotos: (token: string, count: number) =>
    landlordApi.post(`api/portal/hazard-report/${token}/photos/presign`, { count }),
  submitHazardReport: (token: string, data: { hazardType: string; location: string; description: string; photoKeys: string[]; reportedAsEmergency?: boolean }) =>
    landlordApi.post(`api/portal/hazard-report/${token}`, data),
};

// F-016: NLP Analysis Service
export const analysisService = {
  // Check if LLM is available
  getStatus: () => landlordApi.get('api/analysis/status'),

  // Pattern-based analysis (fast, basic)
  scanText: (text: string) => landlordApi.post('api/analysis/scan', { text }),

  // LLM-powered analysis (Claude via Bedrock)
  analyzeLLM: (text: string, useFast: boolean = false) =>
    landlordApi.post('api/analysis/llm', { text, useFast }),

  // Smart analysis (LLM with fallback to pattern)
  analyzeSmartly: (text: string, documentType: string = 'TENANCY_AGREEMENT') =>
    landlordApi.post('api/analysis/smart', { text, documentType }),

  // Analyze S3 document (with optional LLM)
  analyzeS3Document: (s3Key: string, useLLM: boolean = true, documentType: string = 'TENANCY_AGREEMENT') =>
    landlordApi.post('api/analysis/s3', { s3Key, useLLM, documentType })
};
// F-021: Eviction Workflow Service
export const evictionService = {
  getGrounds: () => landlordApi.get('api/evictions/grounds'),

  createCase: (data: { tenantId: string; propertyId: string; groundCode: string; outstandingAmount?: number }) =>
    landlordApi.post('api/evictions', data),

  getCases: () => landlordApi.get('api/evictions'),

  getCase: (id: string) => landlordApi.get(`api/evictions/${id}`),

  updateStep: (id: string, data: { action: string; note?: string; nextStatus?: string }) =>
    landlordApi.put(`api/evictions/${id}/step`, data),
  acknowledgeRisk: (id: string) =>
    landlordApi.put(`api/evictions/${id}/risk`, {}),
  getCaseBundle: (id: string) => landlordApi.get(`api/evictions/${id}/bundle`),
  issueNotice: (id: string, data: { rentArrearsAmount: number; rentPeriod: string }) =>
    landlordApi.post(`api/evictions/${id}/issue-notice`, data),
  markAsServed: (id: string, data: { serviceDate: string; serviceMethod: string; servingAgentName: string }) =>
    landlordApi.post(`api/evictions/${id}/mark-served`, data),
  getCertificateOfService: (id: string) =>
    landlordApi.get(`api/evictions/${id}/certificate-of-service`),
};

// F-017: Template Service
export const templateService = {
  list: () => landlordApi.get('api/templates'),
  get: (id: string) => landlordApi.get(`api/templates/${id}`),
  save: (data: any) => landlordApi.post('api/templates', data),
  update: (id: string, data: any) => landlordApi.put(`api/templates/${id}`, data),
  clone: (id: string, version?: string) => landlordApi.post(`api/templates/${id}/clone`, { version }),
  delete: (id: string) => landlordApi.delete(`api/templates/${id}`),
  getVersions: (id: string) => landlordApi.get(`api/templates/${id}/versions`),
};

// F-002: Tenancy Agreement Service
export const agreementService = {
  list: () => landlordApi.get('api/agreements'),
  get: (id: string) => landlordApi.get(`api/agreements/${id}`),
  upload: (data: { title: string; text: string; propertyId?: string }) => landlordApi.post('api/agreements', data),
  extractText: (payload: { file: string; fileName: string; mimeType: string }) => landlordApi.post('api/agreements/extract-text-b64', payload),
  getVersions: (id: string) => landlordApi.get(`api/agreements/${id}/versions`),
  getVersion: (id: string, version: number) => landlordApi.get(`api/agreements/${id}/versions/${version}`),
  analyze: (id: string, data: { focusAreas?: string[]; strictMode?: boolean }) => landlordApi.post(`api/agreements/${id}/analyze`, data),
  amend: (id: string, data: { amendmentIds: string[]; applyAll?: boolean; regulationRef?: string }) => landlordApi.post(`api/agreements/${id}/amend`, data),
  delete: (id: string) => landlordApi.delete(`api/agreements/${id}`),
};

// F-007: Legal Notice Service
export const legalService = {
  generateNotice: (data: any) => landlordApi.post('api/legal/generate-notice', data),
  calculateNoticeDate: (data: {
    rentPaymentDay: number;
    tenancyStartDate?: string;
    groundType: string;
    intendedServiceDate?: string;
    freePeriodDays?: number;
    freePeriodGranted?: boolean;
  }) => landlordApi.post('api/legal/calculate-notice-date', data),
  getNoticeGrounds: () => landlordApi.get('api/legal/notice-grounds'),
};

// F-020: Communication Service (Reminders)
export const communicationService = {
  sendReminder: (data: { tenantId: string; type: 'friendly' | 'firm' | 'legal'; channel: 'email' | 'sms' | 'both'; message?: string }) =>
    landlordApi.post('api/communications/reminders/send', data),
  sendMessage: (data: { tenantId: string; subject: string; content: string; type: 'email' | 'sms'; scheduledAt?: string; attachment?: { url: string; name: string } }) =>
    landlordApi.post('api/communications/send', data),
  // Returns { uploadUrl (PUT here from browser), readUrl (use as attachment.url) }
  getAttachmentPresignedUrl: (data: { fileName: string; fileType: string }) =>
    landlordApi.post('api/communications/attachment/presigned-url', data),
  // Comms Log (v2.5.6) — every outbound message is persisted
  listTenantMessages: (tenantId: string) =>
    landlordApi.get(`api/communications/tenants/${encodeURIComponent(tenantId)}/messages`),
  listPropertyMessages: (propertyId: string) =>
    landlordApi.get(`api/communications/properties/${encodeURIComponent(propertyId)}/messages`),
  listRecentMessages: (limit: number = 8) =>
    landlordApi.get(`api/communications/recent?limit=${limit}`),
  // F-097 cadence endpoints
  getMissedCadence: () =>
    landlordApi.get('api/vulnerable-comms/cadence/missed'),
  getTenantMissedCadence: (tenantId: string) =>
    landlordApi.get(`api/vulnerable-comms/tenants/${encodeURIComponent(tenantId)}/cadence/missed`),
  scheduleTenantCadence: (tenantId: string, lookAheadDays: number = 14) =>
    landlordApi.post(`api/vulnerable-comms/tenants/${encodeURIComponent(tenantId)}/cadence/schedule`, { lookAheadDays }),
};

// F-022: Finance Service
export const financeService = {
  getSummary: () => landlordApi.get('api/financials/summary'),
  getTransactions: () => landlordApi.get('api/financials'),
  getVATReturn: () => landlordApi.get('api/financials/vat-return'),
  addTransaction: (data: any) => landlordApi.post('api/financials', data),
  deleteTransaction: (id: string, date: string, type: string) =>
    landlordApi.delete(`api/financials/${id}?date=${date}&type=${type}`),
  bulkImport: (transactions: any[]) => landlordApi.post('api/financials/bulk', { transactions }),
};


// F-012: Local Gov / Regulator Service
export const govService = {
  requestAccess: (data: { email: string; borough: string; name: string; role: string }) =>
    landlordApi.post('api/regulator/request-access', data),
};

// Partner Job Service
const getPartnerToken = () => localStorage.getItem('tradePartnerToken');
const pAuth = () => ({ headers: { Authorization: `Bearer ${getPartnerToken()}` } });

export const jobService = {
  // Landlord routes
  orderService: (data: any) => landlordApi.post('api/jobs/order', data),
  getLandlordJobs: () => landlordApi.get('api/jobs'),

  // Partner Admin routes
  getAdminCalendar: () => landlordApi.get('api/jobs/calendar', pAuth()),
  dispatchJob: (jobId: string, assignedPartnerId: string) => landlordApi.post(`api/jobs/${jobId}/dispatch`, { assignedPartnerId }, pAuth()),
  cancelJob: (jobId: string) => landlordApi.post(`api/jobs/${jobId}/cancel`, {}, pAuth()),

  // Partner routes
  getAssignedJobs: () => landlordApi.get('api/jobs/assigned', pAuth()),
  acceptJob: (jobId: string) => landlordApi.post(`api/jobs/${jobId}/accept`, {}, pAuth()),
  declineJob: (jobId: string) => landlordApi.post(`api/jobs/${jobId}/decline`, {}, pAuth()),
  completeJob: (jobId: string) => landlordApi.post(`api/jobs/${jobId}/complete`, {}, pAuth()),
};

// Certificate Orders Service (Phase 1 & 2)
export const orderService = {
  // Get all orders for current tenant
  listOrders: () => landlordApi.get('api/orders'),

  // Get single order by ID
  getOrder: (orderId: string) => landlordApi.get(`api/orders/${orderId}`),

  // Create new certificate order
  createOrder: (data: { certificateType: string; propertyId: string; dateRange: { startDate: string; endDate: string }; accessDetails?: any; notes?: string; tenantName?: string; tenantPhone?: string }) =>
    landlordApi.post('api/orders', data),

  // Bulk import existing/historic orders from a CSV
  bulkImport: (orders: any[]) => landlordApi.post('api/orders-bulk', { orders }),

  // Update order details
  updateOrder: (orderId: string, data: any) => landlordApi.patch(`api/orders/${orderId}`, data),

  // Assign partner to order
  assignPartner: (orderId: string, partnerId: string) =>
    landlordApi.patch(`api/orders/${orderId}/assign-partner`, { partnerId }),

  // Cancel order
  cancelOrder: (orderId: string) => landlordApi.post(`api/orders/${orderId}/cancel`, {}),

  // Admin: Get all orders across all tenants (for calendar)
  getAllOrdersAdmin: () => landlordApi.get('api/orders/admin/all'),

  // Get properties for dropdown
  getProperties: () => landlordApi.get('api/properties'),

  // Get partners available for assignment
  getAvailablePartners: () => landlordApi.get('api/partners/available'),

  // Get payment status for order
  getPaymentStatus: (orderId: string) => landlordApi.get(`api/orders/${orderId}/payment`),

  // Create payment checkout (FastSpring integration)
  createPaymentCheckout: (orderId: string) => landlordApi.post(`api/orders/${orderId}/checkout`, {}),

  // Get order tracking information
  getOrderTracking: (orderId: string) => landlordApi.get(`api/orders/${orderId}/tracking`),
};

// E-014: AI Legal Q&A Engine
export const legalQaService = {
  ask: (question: string, context?: string, useFast?: boolean, analyzeDocument?: boolean) =>
    landlordApi.post('api/qa/ask', { question, context, useFast, analyzeDocument }),
  analyze: (text: string) =>
    landlordApi.post('api/qa/analyze', { text }),
  health: () => landlordApi.get('api/qa/health'),
};

// F-038: Q&A Audit History
export const qaHistoryService = {
    list: () => landlordApi.get('api/qa/history/logs'),
    getById: (id: string) => landlordApi.get(`api/qa/history/logs/${id}`),
    listAll: () => landlordApi.get('api/qa/history/logs/all'),
};

// F-037: Compliance Knowledge Base & Citation Engine
export const knowledgeBaseService = {
  listDocs: () => landlordApi.get('api/knowledge-base/docs'),
  getDoc: (id: string) => landlordApi.get(`api/knowledge-base/docs/${id}`),
  addDoc: (data: { title: string; source?: string; sourceUrl?: string }) =>
    landlordApi.post('api/knowledge-base/docs', data),
  deleteDoc: (id: string) => landlordApi.delete(`api/knowledge-base/docs/${id}`),
  search: (query: string, limit?: number) =>
    landlordApi.post('api/knowledge-base/search', { query, limit }),
  getUploadUrl: (fileName: string) =>
    landlordApi.post('api/knowledge-base/upload-url', { fileName }),
};

// TEXAS COMPLIANCE SERVICE (F-268 / E-049)
export { texasComplianceService } from './texasComplianceService';
